import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createIncident } from '../api/incidents';
import { Incident } from '../types/incident';
import { DuplicateIncidentBanner } from '../components/DuplicateIncidentBanner';

interface ReportIncidentProps {
  onSuccess: (incident: Incident) => void;
  onCancel: () => void;
}

// Neither incident TYPE nor SEVERITY are collected on the form anymore —
// both are assessed by the agent from the description + early SitRep chat:
//   - TYPE via [INCIDENT_TYPE:X] marker (written to cima_incidents.type)
//   - SEVERITY via [SEVERITY_ASSESSMENT:CRITICAL|MODERATE|MINOR] marker
//     (written to cima_incidents.severity by handleSeverityAssessment)
// We default severity to MODERATE on creation; the agent typically
// reclassifies within 30-60s. Yash's call: forcing the reporter to
// guess severity at intake time produced wrong ratings (they tend to
// over-rate for safety) which then biased CIMT activation. Better to
// let the agent do it from the same context an analyst would.
type FormData = {
  title: string;
  description: string;
  location: string;
  occurredAt: string;
  peopleInvolved: string;
  immediateActions: string;
};

const EMPTY_FORM: FormData = {
  title: '',
  description: '',
  location: '',
  occurredAt: '',
  peopleInvolved: '',
  immediateActions: '',
};

// Local datetime → ISO string
function toISO(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

// Default to now in local-datetime format
function nowLocalDatetime(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  // Use local time, not UTC — toISOString() would shift the time
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mi = String(now.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export function ReportIncident({ onSuccess, onCancel }: ReportIncidentProps) {
  const { user } = useAuth0();

  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM, occurredAt: nowLocalDatetime() });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.title.trim() || form.title.trim().length < 3) e.title = 'Title must be at least 3 characters';
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.occurredAt)  e.occurredAt  = 'Date and time is required';
    if (!form.description.trim() || form.description.trim().length < 10)
      e.description = 'Description must be at least 10 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError(null);

    try {
      const incident = await createIncident({
        // type + severity both omitted from the form — the agent
        // assesses them within the first chat turn. Backend defaults:
        // type=UNCLASSIFIED, severity=MODERATE. Agent reclassifies
        // both via [INCIDENT_TYPE:X] + [SEVERITY_ASSESSMENT:X] markers.
        title:            form.title.trim(),
        description:      form.description.trim(),
        location:         form.location.trim(),
        occurredAt:       toISO(form.occurredAt),
        severity:         'MODERATE',
        peopleInvolved:   form.peopleInvolved.trim() || undefined,
        immediateActions: form.immediateActions.trim() || undefined,
        reporterName:     user?.name || user?.email || 'Unknown',
        reporterEmail:    user?.email || '',
        clientId:         1,
      });
      onSuccess(incident);
    } catch (err: any) {
      setApiError(err.message || 'Failed to submit incident. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="report-page">
      <div className="report-layout">
       <div className="report-container" style={{ maxWidth: 'none' }}>

        {/* ── Header ── */}
        <div className="report-header">
          <button className="back-btn" onClick={onCancel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Dashboard
          </button>
          <div style={{ marginTop: 20 }}>
            <h1 className="report-title">Report a Critical Incident</h1>
            <p className="report-sub">
              Complete all required fields. The Incident Commander will be notified immediately upon submission.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate data-tour="report.form">

          <DuplicateIncidentBanner
            title={form.title}
            location={form.location}
            description={form.description}
            onView={onSuccess}
          />

          {/* ── Section 1: Incident details ── */}
          <div className="form-section">
            <div className="form-section-label">
              <span className="form-section-num">1</span>
              Incident Details
            </div>

            {/* Severity is no longer collected here — the agent assesses
                it from the description + early SitRep chat and writes the
                result to cima_incidents.severity via the
                [SEVERITY_ASSESSMENT:X] marker. Removed because reporters
                tend to over-rate severity at intake (better safe than
                sorry), which biased CIMT activation. Letting the agent
                rate severity domain-by-domain produces calibrated
                ratings. Incident TYPE is also agent-assessed, same
                pattern, same reason. */}

            {/* Title */}
            <div className="form-field" data-tour="report.title">
              <label className="field-label">Incident Title <span className="req">*</span></label>
              <input
                type="text"
                className={`field-input ${errors.title ? 'field-error' : ''}`}
                placeholder="Brief summary of what happened…"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                maxLength={150}
              />
              {errors.title && <span className="error-msg">{errors.title}</span>}
            </div>

            <div className="form-grid-2">
              {/* Location */}
              <div className="form-field" data-tour="report.location">
                <label className="field-label">Location <span className="req">*</span></label>
                <input
                  type="text"
                  className={`field-input ${errors.location ? 'field-error' : ''}`}
                  placeholder="Building, floor, site address…"
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                />
                {errors.location && <span className="error-msg">{errors.location}</span>}
              </div>

              {/* Date & Time */}
              <div className="form-field">
                <label className="field-label">Date & Time of Incident <span className="req">*</span></label>
                <input
                  type="datetime-local"
                  className={`field-input ${errors.occurredAt ? 'field-error' : ''}`}
                  value={form.occurredAt}
                  max={nowLocalDatetime()}
                  onChange={(e) => set('occurredAt', e.target.value)}
                />
                {errors.occurredAt && <span className="error-msg">{errors.occurredAt}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="form-field" data-tour="report.description">
              <label className="field-label">Description <span className="req">*</span></label>
              <textarea
                className={`field-textarea ${errors.description ? 'field-error' : ''}`}
                placeholder="Describe exactly what happened, in as much detail as possible…"
                rows={5}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
              {errors.description && <span className="error-msg">{errors.description}</span>}
            </div>
          </div>

          {/* ── Section 2: Additional info ── */}
          <div className="form-section">
            <div className="form-section-label">
              <span className="form-section-num">2</span>
              Additional Information <span className="optional-tag">Optional</span>
            </div>

            {/* People Involved */}
            <div className="form-field">
              <label className="field-label">People Involved</label>
              <input
                type="text"
                className="field-input"
                placeholder="Names of anyone involved or injured…"
                value={form.peopleInvolved}
                onChange={(e) => set('peopleInvolved', e.target.value)}
              />
            </div>

            {/* Immediate Actions */}
            <div className="form-field">
              <label className="field-label">Immediate Actions Taken</label>
              <textarea
                className="field-textarea"
                placeholder="What steps have already been taken to address the incident?…"
                rows={3}
                value={form.immediateActions}
                onChange={(e) => set('immediateActions', e.target.value)}
              />
            </div>
          </div>

          {/* ── Section 3: Reporter ── */}
          <div className="form-section">
            <div className="form-section-label">
              <span className="form-section-num">3</span>
              Reported By
            </div>
            <div className="reporter-card">
              <div className="reporter-avatar">
                {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="reporter-name">{user?.name || 'Unknown User'}</div>
                <div className="reporter-email">{user?.email}</div>
              </div>
              <div className="reporter-auto-tag">Auto-filled from your account</div>
            </div>
          </div>

          {/* ── API Error ── */}
          {apiError && (
            <div className="api-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {apiError}
            </div>
          )}

          {/* ── Submit ── */}
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" data-tour="report.submit" disabled={submitting}>
              {submitting ? (
                <>
                  <div className="mini-spinner" style={{ borderTopColor: '#0a1a18' }} />
                  Submitting…
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  Submit Incident Report
                </>
              )}
            </button>
          </div>

        </form>
       </div>

       {/* ── Side panel — fills the empty space with useful context.
            Reads as a quick reference: 24/7 emergency contact, what happens
            after submit, severity guide, and reporting tips. */}
       <aside className="report-aside">

         <div className="aside-card aside-emergency">
           <div className="aside-alert-head">
             <span className="aside-alert-icon" aria-hidden>
               <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                 <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                 <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
               </svg>
             </span>
             <span className="aside-alert-title">Life-threatening emergency?</span>
           </div>
           <div className="aside-body">
             Don't use this form. Call <strong style={{ color: 'var(--accent-red)' }}>000</strong> first, then come back to log the incident.
           </div>
           <div className="aside-note">
             Crisis Shield 24/7 advisory: <strong style={{ color: 'var(--fg)' }}>+61 417 160 120</strong>
           </div>
         </div>

         <div className="aside-card">
           <div className="aside-eyebrow">What happens next</div>
           <div className="aside-steps">
             <div className="aside-step">
               <span className="aside-step-num">1</span>
               <div className="aside-step-text">
                 <strong>Submit the report</strong>
                 Your report is timestamped and the Incident Commander is paged immediately.
               </div>
             </div>
             <div className="aside-step">
               <span className="aside-step-num">2</span>
               <div className="aside-step-text">
                 <strong>SitRep with the AI agent</strong>
                 For Critical / Moderate, you'll be guided through facts, assumptions and impacts.
               </div>
             </div>
             <div className="aside-step">
               <span className="aside-step-num">3</span>
               <div className="aside-step-text">
                 <strong>CIMT activated</strong>
                 This week's roster is paged and a Teams meeting is auto-scheduled.
               </div>
             </div>
             <div className="aside-step">
               <span className="aside-step-num">4</span>
               <div className="aside-step-text">
                 <strong>Build &amp; sign off the IAP</strong>
                 The agent drafts the action plan from meeting notes — IC reviews and signs.
               </div>
             </div>
           </div>
         </div>

         <div className="aside-card">
           <div className="aside-eyebrow">How severity is set</div>
           <div className="aside-body">
             <div style={{ marginBottom: 10 }}>
               The agent will assess severity from your description in the
               first chat turn — domain-by-domain (people, assets,
               regulatory, reputation, continuity). You don't need to
               pick one here.
             </div>
             <div style={{ marginBottom: 8 }}>
               <strong style={{ color: 'var(--accent-red)' }}>Critical</strong> — life safety, major asset loss, or full operational halt.
             </div>
             <div style={{ marginBottom: 8 }}>
               <strong style={{ color: '#fb923c' }}>Moderate</strong> — partial disruption, recoverable in &lt; 24h.
             </div>
             <div>
               <strong style={{ color: '#4ade80' }}>Minor</strong> — isolated impact, normal ops continue.
             </div>
           </div>
         </div>

         <div className="aside-card">
           <div className="aside-eyebrow">Tips for a clear report</div>
           <ul className="aside-tip-list">
             <li>Stick to facts you've personally verified.</li>
             <li>Note exact location — building, floor, room.</li>
             <li>List people involved by role, not just name.</li>
             <li>Capture immediate actions you've already taken.</li>
           </ul>
         </div>

       </aside>

      </div>
    </div>
  );
}
